import { useTranslation } from "react-i18next";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import Loader from "../../modules/layout/components/Loader";
import { useToast } from "../../modules/layout/components/ToastContext";
import { useEffect, useState } from "react";
import { IAxiosGetResponse, IFilterInstitutionModel, IInstitutionDto } from "../../interfaces/IUtilities";
import { Link, useNavigate } from "react-router-dom";
import SortDirection from "../../modules/layout/components/SortDirection";
import { deleteById, getAll, post } from "../_request";
import { Archive, FileEarmarkText, List, Pencil, Trash3 } from "react-bootstrap-icons";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import { AxiosResponse } from "axios";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import {Field, Form, Formik} from "formik";
import Pagination from "../../modules/layout/components/Pagination";


const InstitutionList: React.FC = () => {

    const {t} = useTranslation();
    const {showToast} = useToast();
    const [loading, setLoading] = useState(true);
    const [institutions, setInstitutions] = useState([] as IInstitutionDto[]);
    const [total, setTotal] = useState(0);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("name");
    const [asc, setAsc] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showRestoreConfiramtionModal, setShowRestoreConfiramtionModal] = useState(false);
    const [selectedName, setSelectedName] = useState<string | null>(null);
   
    const initialFormState = {
        id: '',
        name: '',
        showDeleted: false,
    }
    let order = asc ? "asc" : "desc";
    const [activeFilters, setActiveFilters] = useState<IFilterInstitutionModel>(initialFormState);


    const fetchInstitutions = async (filters: IFilterInstitutionModel = activeFilters) => {
        try {

            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.name) params.push({ key: 'name', value: filters.name });
            if (filters.showDeleted) params.push({ key: 'showDeleted', value: filters.showDeleted === true ? 1 : 0 });

            let result = await getAll('/api/institutions/getall',
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as IInstitutionDto[];
            setTotal(result?.data.totalCount);

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setInstitutions(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };
    
    const handleFilterSearch = async (filters: IFilterInstitutionModel) => {
        setActiveFilters(filters);
        await fetchInstitutions(filters);
    };

    useEffect(() => {
        fetchInstitutions();
    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const handleDeleteClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowModal(true);
    };

    const changeShowDeteled = async (event: any) => {
        event.preventDefault();
        setActiveFilters({
            ...activeFilters,
            showDeleted: !activeFilters.showDeleted
        })
    }


    const handleConfirmDelete = async () => {
        if (selectedId) {
            setLoading(true);
            try {
                let result = await deleteById("/api/institutions", selectedId) as AxiosResponse;
                if (result.status === 200) {
                    showToast(t('InstitutionDeletedSuccessfully'), 'success');
                    setLoading(false);
                    await fetchInstitutions();
                } else if (result.status === 409) {
                    setLoading(false);
                    showToast(t('InstitutionIsNotEmpty'), 'error');
                } else if (result.status === 403) {
                    navigate("/error/403");
                } else if (result.status === 401) {
                    navigate("/auth/login");
                } else if (result.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }

        }
        setShowModal(false);
        setSelectedId(null);
    };

    const handleCancelConfirm = () => {
        setShowModal(false);
        setSelectedId(null);
    };

    const handleRecoveryClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowRestoreConfiramtionModal(true);
    };
    
    const handleRecoverSelection = async () => {
        // Start the restoration request
        if (selectedId) {
            setLoading(true);
            try {
                let result = await post(`/api/institutions/restore/${selectedId}`, {}) as IAxiosGetResponse;
                if (result?.response.status === 200) {
                    showToast(t('InstitutionRestoredSuccessfully'), 'success');
                    setLoading(false);
                    setSelectedId(null);
                    setShowRestoreConfiramtionModal(false);
                    await fetchInstitutions();
                } else if (result.response.status === 403) {
                    navigate("/error/403");
                } else if (result?.response.status === 401) {
                    navigate("/auth/login");
                } else if (result?.response.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result?.response.status)}));
                }
            } catch (error) {
                console.log(t('ConnectionError', {error: String(error)}));
                setLoading(false);
                setShowRestoreConfiramtionModal(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        }
    }
    

    return (
        <div className="gg-serviceList container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: "/" },
                    { title: t('InstitutionList') } 
                ]}/>

            <h3 className="mb-3">{ t('Institutions')}</h3>

            {loading && (<Loader />)}

            <button className="btn btn-success gg-btn-submit mb-3" onClick={() => navigate("/institutions/create")}>
                {t('Create')}
            </button>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm }) => (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('Id')}
                                        <Field className="form-control" name="id"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('Name')}
                                        <Field className="form-control" name="name"/>
                                    </label>
                                </div>                 
                                <div className="col-md-6 col-lg-6 col-xl-4 align-self-end d-flex align-items-center">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        { t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState);
                                            }}>
                                        {t('Reset')}
                                    </button>
                                    <label className="form-label w-100 ms-2 small">
                                        <Field type="checkbox" className="form-check-input me-2" name="showDeleted"
                                        checked={activeFilters.showDeleted}
                                        onChange={(e: any) => changeShowDeteled(e)} />
                                        {t('ShowDeleted')}
                                    </label>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>



            <div className="table-responsive">
                <table className="table">
                    <thead>
                    <tr>
                        <th style={{width: "25%"}} onClick={() => handleSort("name")}>{t('Name')}
                            <SortDirection isActive={sortField === 'name'} order={order}/>
                        </th>
                        <th className="text-wrap" style={{width: "30%"}}>{t('Actions')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {institutions.map(s => (
                        <tr key={s.id}>
                            <td>{s.name}</td>
                            <td className="text-wrap">
                                {s.isDeleted ? <>
                                        <button title={t('Restore')} className="btn btn-warning btn-sm me-2"
                                            onClick={() => handleRecoveryClick(s.id, s.name)}>
                                            <Archive size={16} />
                                        </button>
                                </> : <>
                                    <Link title={t('Details')} to={`/institutions/details/${s.id}`}
                                        className="btn gg-btn btn-sm me-2">
                                        <FileEarmarkText size={16}/>
                                    </Link>
                                    <Link title={t('Edit')} to={`/institutions/edit/${s.id}`} className="btn gg-btn btn-sm me-2">
                                        <Pencil size={16} className="text-danger" />
                                    </Link>
                                        <button title={t('Delete')} className="btn btn-danger btn-sm me-2"
                                                onClick={() => handleDeleteClick(s.id, s.name)}>
                                            <Trash3 size={16}/>
                                        </button>
                                    <Link title={t('Institutions')} to={`/services/list/${s.id}`}
                                        className="btn btn-sm btn-warning me-2">
                                        <List size={16} />
                                    </Link>
                                </>}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                show={showModal}
                loading={loading}
                title={t('Delete')}
                message={t('InstitutionDeletionConfirmation', {selectedName})}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelConfirm}
            />

            <Pagination currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                totalItems={total}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size) }} />

            <ConfirmModal
                show={showRestoreConfiramtionModal}
                title={t('Restore')}
                message={t('ItemRestoreConfirmation', { selectedName })}
                onConfirm={handleRecoverSelection}
                onCancel={handleCancelConfirm}
            />
        </div>
    );
}
export default InstitutionList;